import type { Caller } from "@repo/common";
import { db } from "@repo/ui";
import {
	collectionGroup,
	onSnapshot,
	orderBy,
	query,
} from "firebase/firestore";
import { Loader2, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useData } from "../contexts/DataContext";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

export interface CallerWithMeta extends Caller {
	id: string;
	companyName: string;
}

export function CallersPage({
	onNavigateToCaller,
}: {
	onNavigateToCaller: (companyId: string, callerId: string) => void;
}) {
	const [callers, setCallers] = useState<CallerWithMeta[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCompanyId, setSelectedCompanyId] = useState("all");
	const { companies } = useData();

	useEffect(() => {
		const q = query(
			collectionGroup(db, "callers"),
			orderBy("updatedAt", "desc"),
		);

		const unsub = onSnapshot(q, (snap) => {
			const fetched: CallerWithMeta[] = [];
			snap.forEach((doc) => {
				const data = doc.data() as Caller;
				const companyId = doc.ref.parent.parent?.id;
				const companyName =
					companies.find((c) => c.id === companyId)?.name ||
					companyId ||
					"Unknown";

				fetched.push({
					...data,
					id: doc.id,
					companyName,
					companyId: companyId as string,
				});
			});
			setCallers(fetched);
			setIsLoading(false);
		});

		return () => unsub();
	}, [companies]);

	const filteredCallers = useMemo(() => {
		return callers.filter((c) => {
			if (selectedCompanyId !== "all" && c.companyId !== selectedCompanyId) {
				return false;
			}

			const search = searchTerm.toLowerCase();
			return (
				c.name?.toLowerCase().includes(search) ||
				c.phoneNumber?.includes(search) ||
				c.companyName.toLowerCase().includes(search)
			);
		});
	}, [callers, searchTerm, selectedCompanyId]);

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Callers & Memory</h1>
				<p className="text-muted-foreground">
					Manage caller identities and their long-term semantic memory across
					all companies.
				</p>
			</div>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between pb-2">
					<CardTitle>Global Caller Registry</CardTitle>
					<div className="flex items-center gap-4">
						<div className="relative w-64">
							<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
							<Input
								type="search"
								placeholder="Search callers..."
								className="pl-8"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</div>
						<Select
							value={selectedCompanyId}
							onValueChange={setSelectedCompanyId}
						>
							<SelectTrigger className="w-[200px]">
								<SelectValue placeholder="Filter by Company" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Companies</SelectItem>
								{companies.map((company) => (
									<SelectItem key={company.id} value={company.id}>
										{company.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex justify-center p-8">
							<Loader2 className="w-6 h-6 animate-spin text-slate-400" />
						</div>
					) : (
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Caller</TableHead>
										<TableHead>Company</TableHead>
										<TableHead>Calls</TableHead>
										<TableHead>Last Call</TableHead>
										<TableHead>Memory Status</TableHead>
										<TableHead>Updated</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{filteredCallers.map((caller) => (
										<TableRow
											key={`${caller.companyId}-${caller.id}`}
											className="cursor-pointer hover:bg-slate-50"
											onClick={() =>
												onNavigateToCaller(caller.companyId, caller.id)
											}
										>
											<TableCell>
												<div className="font-medium">
													{caller.name || "Unknown"}
												</div>
												<div className="text-sm text-slate-500">
													{caller.phoneNumber}
												</div>
											</TableCell>
											<TableCell>{caller.companyName}</TableCell>
											<TableCell>{caller.callCount}</TableCell>
											<TableCell>
												{caller.lastCallAt
													? new Date(caller.lastCallAt).toLocaleDateString()
													: "Never"}
											</TableCell>
											<TableCell>
												{caller.memory ? (
													<Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200">
														v{caller.memory.version} ({caller.memory.language})
													</Badge>
												) : (
													<Badge variant="outline" className="text-slate-500">
														No Memory
													</Badge>
												)}
											</TableCell>
											<TableCell className="text-sm text-slate-500">
												{caller.memory?.updatedAt
													? new Date(caller.memory.updatedAt).toLocaleString()
													: "-"}
											</TableCell>
										</TableRow>
									))}
									{filteredCallers.length === 0 && (
										<TableRow>
											<TableCell
												colSpan={6}
												className="text-center py-8 text-slate-500"
											>
												No callers found matching your search.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
